import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { GripVertical, Eye, EyeOff, Pencil, Plus, Trash2, Save, ExternalLink } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SortableNavItem({ link, onToggle, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: link.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
        link.enabled
          ? 'bg-zinc-900 border-zinc-800'
          : 'bg-zinc-900/50 border-zinc-800/50 opacity-60'
      }`}
      data-testid={`nav-item-${link.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 text-zinc-500 hover:text-zinc-300 cursor-grab active:cursor-grabbing"
        data-testid={`drag-nav-${link.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${link.enabled ? 'text-white' : 'text-zinc-500'}`}>
          {link.label}
        </p>
        <p className="text-xs text-zinc-600 truncate">{link.path}</p>
      </div>

      {link.type === 'dropdown' && (
        <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">dropdown</span>
      )}

      <div className="flex items-center gap-1">
        <button
          onClick={() => onToggle(link.id)}
          className={`p-1.5 rounded transition-colors ${
            link.enabled ? 'text-[#78BE20] hover:bg-[#78BE20]/10' : 'text-zinc-600 hover:bg-zinc-800'
          }`}
          title={link.enabled ? 'Hide from navigation' : 'Show in navigation'}
          data-testid={`toggle-nav-${link.id}`}
        >
          {link.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
        <button
          onClick={() => onEdit(link)}
          className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
          data-testid={`edit-nav-${link.id}`}
        >
          <Pencil className="h-4 w-4" />
        </button>
        {link.type !== 'dropdown' && (
          <button
            onClick={() => onDelete(link.id)}
            className="p-1.5 text-zinc-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
            data-testid={`delete-nav-${link.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminNavigation() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [newLink, setNewLink] = useState({ label: '', path: '/', enabled: true });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { fetchNavigation(); }, []);

  const fetchNavigation = async () => {
    try {
      const res = await axios.get(`${API}/admin/navigation`);
      setLinks(res.data.links || []);
    } catch (err) {
      console.error('Error fetching navigation:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveNavigation = async (updatedLinks) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/navigation`, { links: updatedLinks || links });
      toast.success('Navigation saved');
      setHasChanges(false);
    } catch (err) {
      toast.error('Failed to save navigation');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (id) => {
    const updated = links.map(l => l.id === id ? { ...l, enabled: !l.enabled } : l);
    setLinks(updated);
    saveNavigation(updated);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = links.findIndex(l => l.id === active.id);
    const newIndex = links.findIndex(l => l.id === over.id);
    const reordered = arrayMove(links, oldIndex, newIndex);
    setLinks(reordered);
    saveNavigation(reordered);
  };

  const handleEdit = (link) => {
    setEditingLink({ ...link });
    setEditDialog(true);
  };

  const handleSaveEdit = () => {
    const updated = links.map(l => l.id === editingLink.id ? editingLink : l);
    setLinks(updated);
    setEditDialog(false);
    saveNavigation(updated);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Remove this navigation link?')) return;
    const updated = links.filter(l => l.id !== id);
    setLinks(updated);
    saveNavigation(updated);
  };

  const handleAddLink = () => {
    if (!newLink.label.trim() || !newLink.path.trim()) {
      toast.error('Label and path are required');
      return;
    }
    const id = newLink.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const updated = [...links, { id, label: newLink.label, path: newLink.path, type: 'link', enabled: newLink.enabled }];
    setLinks(updated);
    setAddDialog(false);
    setNewLink({ label: '', path: '/', enabled: true });
    saveNavigation(updated);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-navigation">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-black text-white uppercase tracking-tight">Navigation</h1>
            <p className="text-zinc-400 mt-1 text-sm">Manage header navigation links - drag to reorder, toggle visibility</p>
          </div>
          <Button onClick={() => setAddDialog(true)} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="add-nav-link-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Link
          </Button>
        </div>

        {/* Preview */}
        <div className="mb-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Header Preview</p>
          <div className="flex items-center gap-6 py-2">
            {links.filter(l => l.enabled).map(link => (
              <span key={link.id} className="text-sm text-zinc-300 font-medium flex items-center gap-1">
                {link.label}
                {link.type === 'dropdown' && <span className="text-zinc-600 text-xs">v</span>}
              </span>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-400">Loading...</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={links.map(l => l.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {links.map(link => (
                  <SortableNavItem
                    key={link.id}
                    link={link}
                    onToggle={handleToggle}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {links.length === 0 && !loading && (
          <div className="text-center py-12 text-zinc-500">
            <p>No navigation links configured.</p>
            <Button onClick={() => setAddDialog(true)} className="mt-4 bg-[#78BE20] text-black hover:bg-[#65a31b]">
              <Plus className="h-4 w-4 mr-2" /> Add Your First Link
            </Button>
          </div>
        )}

        {/* Edit Link Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Navigation Link</DialogTitle>
            </DialogHeader>
            {editingLink && (
              <div className="space-y-4 mt-2">
                <div>
                  <Label>Label</Label>
                  <Input
                    value={editingLink.label}
                    onChange={(e) => setEditingLink({ ...editingLink, label: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    data-testid="edit-nav-label"
                  />
                </div>
                <div>
                  <Label>Path</Label>
                  <Input
                    value={editingLink.path}
                    onChange={(e) => setEditingLink({ ...editingLink, path: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    placeholder="/products"
                    data-testid="edit-nav-path"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
                  <Button onClick={handleSaveEdit} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="save-edit-nav">
                    <Save className="h-4 w-4 mr-2" /> Save
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Link Dialog */}
        <Dialog open={addDialog} onOpenChange={setAddDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Add Navigation Link</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Label</Label>
                <Input
                  value={newLink.label}
                  onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="e.g., Blog"
                  data-testid="add-nav-label"
                />
              </div>
              <div>
                <Label>Path</Label>
                <Input
                  value={newLink.path}
                  onChange={(e) => setNewLink({ ...newLink, path: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="/blog"
                  data-testid="add-nav-path"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button>
                <Button onClick={handleAddLink} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="confirm-add-nav">
                  <Plus className="h-4 w-4 mr-2" /> Add Link
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
